import { ProviderOption, SelectedParcelAccount } from "@RequestFormV2/sections/Provider/types";
import { buildAllProviderSelections } from "@RequestFormV2/sections/Provider/utils/buildAllProviderSelections";
import {
  getProviderSelectionKey,
  isProviderEntrySelected,
} from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";

export function isAllProvidersSelected(
  providers: ProviderOption[],
  selectedProviders: string[],
  selectedParcelAccounts: SelectedParcelAccount[],
): boolean {
  if (providers.length === 0) {
    return false;
  }

  return providers.every((provider) => {
    if (provider.parcel_accounts.length > 0) {
      return (
        isProviderEntrySelected(provider, selectedProviders) &&
        selectedParcelAccounts.some((account) => isProviderEntrySelected(provider, [account.provider_id]))
      );
    }

    return isProviderEntrySelected(provider, selectedProviders);
  });
}

/**
 * Under ALL_OPTIONAL, merge providers that appear after the initial options load
 * into the current selection only when every still-available previous provider
 * remains selected (user has not manually deselected anyone).
 */
export function mergeLateAppearingProvidersIfAllPreviousSelected(
  previousProviderKeys: string[],
  providerOptions: ProviderOption[],
  selectedProviders: string[],
  selectedParcelAccounts: SelectedParcelAccount[],
): { providers: string[]; parcel_accounts: SelectedParcelAccount[] } | null {
  if (previousProviderKeys.length === 0 || providerOptions.length === 0) {
    return null;
  }

  const previousKeySet = new Set(previousProviderKeys);
  const lateProviders = providerOptions.filter((provider) => !previousKeySet.has(getProviderSelectionKey(provider)));

  if (lateProviders.length === 0) {
    return null;
  }

  const stillAvailablePrevious = providerOptions.filter((provider) =>
    previousKeySet.has(getProviderSelectionKey(provider)),
  );

  if (stillAvailablePrevious.length === 0) {
    return null;
  }

  if (!isAllProvidersSelected(stillAvailablePrevious, selectedProviders, selectedParcelAccounts)) {
    return null;
  }

  const { providers: lateProviderKeys, parcel_accounts: lateParcelAccounts } =
    buildAllProviderSelections(lateProviders);

  const providers = [...selectedProviders];
  lateProviderKeys.forEach((key) => {
    if (!providers.some((entry) => String(entry) === key)) {
      providers.push(key);
    }
  });

  const parcel_accounts = [...selectedParcelAccounts];
  lateParcelAccounts.forEach((account) => {
    if (
      !parcel_accounts.some(
        (existing) =>
          String(existing.provider_id) === String(account.provider_id) &&
          existing.parcel_account_id === account.parcel_account_id,
      )
    ) {
      parcel_accounts.push({
        provider_id: String(account.provider_id),
        parcel_account_id: account.parcel_account_id,
      });
    }
  });

  return { providers, parcel_accounts };
}
